import { endOfDay, startOfDay, sub, toDate } from 'date-fns';
import { cloneDeep, extend, has, isArray, isEmpty, isNumber, isObject, map, uniq, } from 'lodash';
import { NOT_FULL_DAY_FILTERS, TIME_SELECTIONS } from './constants';
import { coalesceFieldName } from 'utils/coalesceLegacyAndNewFields';
export function getMinValidDate() {
    return startOfDay(sub(new Date(), { months: 12 })); // Data available 365 days back
}
export function getInitialDateRange() {
    const today = new Date();
    const dateFrom = toDate(startOfDay(sub(new Date(), { months: 4 }))).toISOString();
    const dateTo = today.toISOString();
    return {
        dateFrom,
        dateTo,
    };
}
function getCreatedAtDefaultFilterModel() {
    return {
        created_at: Object.assign(Object.assign({}, getInitialDateRange()), { filterType: 'date', type: 'inRange', filterParams: {
                minValidDate: getMinValidDate(),
            } }),
    };
}
function replaceNulls(arr) {
    return arr.map((val) => (val === null ? '' : val));
}
const drayageFilterModel = {
    shipment_type: {
        condition1: {
            type: 'equals',
            filter: 'drayage',
            filterType: 'text',
        },
        condition2: {
            type: 'equals',
            filter: 'rail',
            filterType: 'text',
        },
        operator: 'OR',
    },
};
const shorthaulFilterModel = {
    shipment_type: {
        condition1: {
            type: 'equals',
            filter: 'shorthaul',
            filterType: 'text',
        },
        condition2: {
            type: 'equals',
            filter: 'longhaul',
            filterType: 'text',
        },
        condition3: {
            type: 'equals',
            filter: 'dayrate',
            filterType: 'text',
        },
        operator: 'OR',
    },
};
/*
 * multiLineFilter is used to create a query string for multi-line items
 * @parms{Object} filterModel is the actual filter
 * @params{String} field is the name of the field
 * @returns an object with conditions for each word to be queried
 * */
function makeMultiItemFilter(field, filterModel) {
    const conditionMap = filterModel.reduce((map, val, i) => {
        map[`condition${i + 1}`] = {
            filter: val[field],
            type: 'equals',
            filterType: 'text',
        };
        return map;
    }, {});
    conditionMap.operator = 'OR';
    return {
        [field]: conditionMap,
    };
}
// Used for TIME_SELECTIONS range list
const calculateDateFilterRange = (model) => {
    const rangeParams = TIME_SELECTIONS.find((item) => item.localizationSlug === model.type || item.name === model.type);
    if (rangeParams) {
        return {
            dateFrom: rangeParams.start().toISOString(),
            dateTo: rangeParams.end().toISOString(),
            filterType: 'date',
            // setting custom type to prevent overrides, standard will be returned in APIUtils before actual API call
            type: NOT_FULL_DAY_FILTERS.includes(model.type)
                ? 'timeSelectionInRange'
                : 'inRange',
        };
    }
    return model;
};
export function getModelParams({ view, filterModel, agGridApi }) {
    let modelParams = view === 'drayage'
        ? extend({}, drayageFilterModel, filterModel)
        : extend({}, shorthaulFilterModel, filterModel);
    if (isEmpty(modelParams.created_at)) {
        const createdAtDefaultFilterModel = getCreatedAtDefaultFilterModel();
        const createdAtFilterInstance = agGridApi.getFilterInstance('created_at');
        if (!isEmpty(createdAtFilterInstance)) {
            createdAtFilterInstance.setModel(createdAtDefaultFilterModel === null || createdAtDefaultFilterModel === void 0 ? void 0 : createdAtDefaultFilterModel.created_at);
        }
        modelParams = extend({}, createdAtDefaultFilterModel, modelParams);
    }
    const fieldValues = Object.keys(filterModel) || [];
    fieldValues.forEach((field) => {
        let filterValues = [];
        let textFilters = [];
        // Change model of TIME_SELECTIONS date filters to inRange based on calculated dates
        if (modelParams[field].filterType === 'date') {
            if (has(modelParams[field], 'conditions')) {
                // Why do we need condition1 and condition2 if we have an array of conditions?
                modelParams[field].condition1 = calculateDateFilterRange(modelParams[field].condition1);
                modelParams[field].condition2 = calculateDateFilterRange(modelParams[field].condition2);
                modelParams[field].conditions = modelParams[field].conditions.map((condition) => calculateDateFilterRange(condition));
            }
            else {
                modelParams[field] = calculateDateFilterRange(modelParams[field]);
            }
        }
        if (has(filterModel[field], 'filterModels')) {
            filterModel[field].filterModels.forEach((val) => {
                if (has(val, 'value')) {
                    filterValues.push(val.value);
                }
                else if (has(val, 'filter')) {
                    textFilters.push(val);
                }
                if (val) {
                    const conditions = Object.keys(val).filter((key) => key.startsWith('condition'));
                    if (conditions.length) {
                        conditions.forEach((conditionKey) => {
                            textFilters.push(val[conditionKey]);
                        });
                    }
                }
            });
        }
        if (has(filterModel[field], 'value')) {
            const value = filterModel[field].value;
            filterValues.push(value);
        }
        if (filterValues.length > 0) {
            filterValues.forEach((value) => {
                if (isObject(value) && !isArray(value)) {
                    if (value.includeText) {
                        modelParams[field] = {
                            type: 'startsWith',
                            filter: value.includeText,
                        };
                    }
                    else if (value.excludeText) {
                        modelParams[field] = {
                            type: 'notStartsWith',
                            filter: value.excludeText,
                        };
                    }
                    else {
                        delete modelParams[field];
                    }
                }
                else {
                    const multiItemFilter = value || [];
                    if (multiItemFilter.length > 0) {
                        const equipParam = makeMultiItemFilter(field, multiItemFilter);
                        delete modelParams[field];
                        modelParams = extend({}, equipParam, modelParams);
                    }
                    else {
                        delete modelParams[field];
                    }
                }
            });
        }
        if (textFilters.length > 0) {
            if (has(modelParams[field], 'filterModels')) {
                delete modelParams[field].filterModels;
            }
            const existing = modelParams[field];
            Object.keys(existing).forEach((prop) => {
                if (prop.includes('condition')) {
                    textFilters.push(existing[prop]);
                }
            });
            const conditionMap = textFilters.reduce((map, val, i) => {
                map[`condition${i + 1}`] = Object.assign({}, val);
                return map;
            }, {});
            // overriding operator in any case so make sure text filter has other options supressed
            conditionMap.operator = 'OR';
            const equipParam = {
                [field]: conditionMap,
            };
            delete modelParams[field];
            modelParams = extend({}, equipParam, modelParams);
        }
    });
    return modelParams;
}
function transformDateCondition(incoming = {}) {
    var _a, _b, _c, _d;
    const model = cloneDeep(incoming);
    const conditionsThatUseStartOfDayOnly = [
        'equals',
        'notEqual',
        'before',
        'after',
    ];
    if (model.filterType !== 'date') {
        return model;
    }
    if (model.type !== 'timeSelectionInRange') {
        model.dateFrom = model.dateFrom
            ? toDate(startOfDay(model.dateFrom)).toISOString()
            : null;
        if (conditionsThatUseStartOfDayOnly.includes(model.type)) {
            model.dateTo = model.dateFrom
                ? (_a = toDate(endOfDay(model.dateFrom))) === null || _a === void 0 ? void 0 : _a.toISOString()
                : null;
        }
        else {
            model.dateTo = model.dateTo
                ? (_b = toDate(endOfDay(model.dateTo))) === null || _b === void 0 ? void 0 : _b.toISOString()
                : null;
        }
    }
    else {
        if (model.dateFrom) {
            model.dateFrom = (_c = new Date(model.dateFrom)) === null || _c === void 0 ? void 0 : _c.toISOString();
        }
        if (model.dateTo) {
            model.dateTo = (_d = new Date(model.dateTo)) === null || _d === void 0 ? void 0 : _d.toISOString();
        }
    }
    return model;
}
export function transformFilterModelQuery(incoming = {}) {
    var _a, _b, _c, _d, _e, _f;
    const dateModel = cloneDeep(incoming);
    const newModel = {};
    map(dateModel, (model, key) => {
        // two or more conditions are changed into "date" : {"condition1" : {"filterType": "date"}, "condition2" : {"filterType": "date"}}
        if (model.conditions || model.condition1 || model.condition2) {
            Object.keys(model).forEach((objKey) => {
                if (objKey === 'conditions') {
                    model.conditions = model.conditions.map((condition) => {
                        return transformDateCondition(condition);
                    });
                }
                else if (objKey.startsWith('condition')) {
                    model[objKey] = transformDateCondition(model[objKey]);
                }
            });
        }
        // in case of only one condition "date": {"filterType": "date"}
        else if (model.filterType === 'date') {
            model = transformDateCondition(model);
        }
        if (!isEmpty(model)) {
            newModel[key] = model;
        }
    });
    if (newModel.shipper_name) {
        let equalsConditions = {};
        let nonEqualsConditions = {};
        Object.entries(newModel.shipper_name).forEach(([key, condition]) => {
            if (condition.type === 'equals') {
                equalsConditions[key] = condition;
            }
            else {
                nonEqualsConditions[key] = condition;
            }
        });
        newModel.shipper_oid = Object.assign(Object.assign(Object.assign({}, equalsConditions), newModel.shipper_oid), { operator: newModel.shipper_name.operator });
        newModel.shipper_name = Object.assign(Object.assign({}, nonEqualsConditions), { operator: newModel.shipper_name.operator });
    }
    if ((_b = (_a = newModel.carrier_subsidiary) === null || _a === void 0 ? void 0 : _a.values) === null || _b === void 0 ? void 0 : _b.includes(null)) {
        const carrierSubsidiaryValues = (_c = newModel === null || newModel === void 0 ? void 0 : newModel.carrier_subsidiary) === null || _c === void 0 ? void 0 : _c.values;
        newModel.carrier_subsidiary.values = replaceNulls(carrierSubsidiaryValues);
    }
    if ((_e = (_d = newModel.shipper_subsidiary) === null || _d === void 0 ? void 0 : _d.values) === null || _e === void 0 ? void 0 : _e.includes(null)) {
        const shipperSubsidiaryValues = (_f = newModel === null || newModel === void 0 ? void 0 : newModel.shipper_subsidiary) === null || _f === void 0 ? void 0 : _f.values;
        newModel.shipper_subsidiary.values = replaceNulls(shipperSubsidiaryValues);
    }
    return newModel;
}
export function loadColumnState(incomingFilter, api, blacklistedColumns = [], unifiedViewV2 = false) {
    var _a, _b, _c, _d, _e, _f, _g;
    let filter = Object.assign({}, incomingFilter);
    if (unifiedViewV2) {
        // Coalesce legacy field names in saved report column state
        filter.columnOrder = ((_a = filter.columnOrder) === null || _a === void 0 ? void 0 : _a.map(coalesceFieldName)) || [];
        filter.hidden = ((_b = filter.hidden) === null || _b === void 0 ? void 0 : _b.map(coalesceFieldName)) || [];
        filter.pinned =
            ((_c = filter.pinned) === null || _c === void 0 ? void 0 : _c.map((p) => (Object.assign(Object.assign({}, p), { field: coalesceFieldName(p.field) })))) || [];
        filter.rowColumnsSize =
            ((_d = filter.rowColumnsSize) === null || _d === void 0 ? void 0 : _d.map((r) => (Object.assign(Object.assign({}, r), { field: coalesceFieldName(r.field) })))) || [];
        filter.rowColumnsSorting =
            ((_e = filter.rowColumnsSorting) === null || _e === void 0 ? void 0 : _e.map((r) => (Object.assign(Object.assign({}, r), { field: coalesceFieldName(r.field) })))) || [];
    }
    filter.hidden = uniq([...((_f = filter.hidden) !== null && _f !== void 0 ? _f : []), ...blacklistedColumns]);
    const columnState = api.getColumnState();
    const visibleColumns = ((_g = filter.columnOrder) === null || _g === void 0 ? void 0 : _g.length) > 0
        ? filter.columnOrder
            .reduce((orderedColumns, colId) => {
            const col = columnState.find((col) => col.colId === colId);
            if (col) {
                return orderedColumns.concat(col);
            }
            return orderedColumns;
        }, [])
            .map((col) => {
            const hide = filter.hidden.includes(col.colId);
            return Object.assign(Object.assign({}, col), { hide });
        })
        : columnState;
    const hiddenColumns = columnState
        .filter((col) => {
        return (filter.hidden.includes(col.field) &&
            !filter.columnOrder.includes(col.field));
    })
        .map((col) => (Object.assign(Object.assign({}, col), { hide: true })));
    const otherColumns = columnState
        .filter((col) => {
        return !filter.columnOrder.includes(col.colId);
    })
        .map((col) => (Object.assign(Object.assign({}, col), { hide: true })));
    const updatedColumnState = visibleColumns
        .concat(...hiddenColumns, ...otherColumns)
        .map((col) => {
        const pinnedColumn = filter.pinned.find((pinnedCol) => pinnedCol.field === col.colId);
        const pinned = pinnedColumn ? pinnedColumn.pinned : false;
        return Object.assign(Object.assign({}, col), { pinned });
    });
    const updatedColumnStateWithWidth = updatedColumnState.map((item) => {
        var _a, _b;
        return (Object.assign(Object.assign({}, item), { colId: item.colId, width: ((_b = (_a = filter === null || filter === void 0 ? void 0 : filter.rowColumnsSize) === null || _a === void 0 ? void 0 : _a.find((x) => x.field === item.colId)) === null || _b === void 0 ? void 0 : _b.size) ||
                item.width }));
    });
    const updatedColumnStateWithWidthAndSorting = updatedColumnStateWithWidth.map((item) => {
        var _a, _b, _c, _d, _e, _f;
        return (Object.assign(Object.assign({}, item), { colId: item.colId, sort: ((_b = (_a = filter === null || filter === void 0 ? void 0 : filter.rowColumnsSorting) === null || _a === void 0 ? void 0 : _a.find((x) => x.field === item.colId)) === null || _b === void 0 ? void 0 : _b.sort) ||
                item.sort, sortIndex: isNumber((_d = (_c = filter === null || filter === void 0 ? void 0 : filter.rowColumnsSorting) === null || _c === void 0 ? void 0 : _c.find((x) => x.field === item.colId)) === null || _d === void 0 ? void 0 : _d.sortIndex)
                ? (_f = (_e = filter === null || filter === void 0 ? void 0 : filter.rowColumnsSorting) === null || _e === void 0 ? void 0 : _e.find((x) => x.field === item.colId)) === null || _f === void 0 ? void 0 : _f.sortIndex
                : null }));
    });
    api.applyColumnState({
        state: updatedColumnStateWithWidthAndSorting,
        applyOrder: true,
    });
}
